import React, { useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { toolbarButtonSx } from "../../../components/common/toolbarStyles";
import ListIcon from "@mui/icons-material/List";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import { useCreatePerson } from "../hooks/useCreatePerson";
import { Person } from "../types";
import PersonFields from "./PersonFields";
import { contentCardSx } from "../../../components/common/surfaceStyles";

const emptyForm: Person = {
  name: "",
  dob: "",
  qualification: "",
  occupation: "",
  monthlyIncome: undefined,
  mobileNo: "",
  email: "",
};

/**
 * Student → Guardians → Add: a father, mother, guardian or emergency contact, not tied to one
 * student yet (linked from the student's page). After a save the form is cleared for the next one.
 */
export default function PersonForm() {
  const navigate = useNavigate();
  const notify = useNotify();
  const { create, saving } = useCreatePerson();
  const [form, setForm] = useState<Person>(emptyForm);
  // Bumped after a save so the fields remount clean (mobile forgets it was touched).
  const [formKey, setFormKey] = useState(0);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const failure = await create(form);
    if (failure) {
      notify(failure, "error");
      return;
    }
    notify("Saved successfully.", "success");
    setForm(emptyForm);
    setFormKey((key) => key + 1);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2, mr: 2 }}>
      <Box display="flex" alignItems="center" gap={2} sx={{ borderBottom: 1, borderColor: "divider", pb: 1 }}>
        <Typography variant="h6" fontWeight={600}>
          Guardians
        </Typography>
        <Box flexGrow={1} />
        <Button variant="outlined" size="small" sx={toolbarButtonSx} startIcon={<ListIcon />} onClick={() => navigate("/app/students/guardians")}>
          Directory
        </Button>
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ ...contentCardSx, display: "flex", flexDirection: "column", gap: 2, ml: { xs: 0, md: "100px" }, mt: 2, maxWidth: 800 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Add
          </Typography>
          <Typography variant="body2" color="text.secondary">
            A father, mother, guardian or emergency contact. Link them to a student from the student's page.
          </Typography>
        </Box>

        <Box key={formKey} display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
          <PersonFields value={form} onChange={setForm} />
        </Box>

        <Box display="flex" gap={1}>
          <Button variant="contained" type="submit" disabled={saving}>
            {saving ? <CircularProgress size={18} /> : "Add"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
